/**
 * SearchableCombobox - Iter 289.p (Carlos, feb-2026)
 *
 * FIX definitivo bug OC Express: Radix Dialog aplica `aria-hidden="true"` a
 * los hermanos del portal del modal y bloquea `pointer-events` en cualquier
 * cosa que no esté dentro del `[role="dialog"]`. Por eso al portalizar el
 * dropdown a `document.body` los clicks no llegaban a las opciones aunque
 * visualmente estuvieran encima (hit testing devolvía contenido del modal).
 *
 * Solución de esta iteración (289):
 *   1. Portal al `[role="dialog"]` ancestor del combobox — así el dropdown
 *      queda DENTRO del stacking context del modal Y dentro del contenedor
 *      accesible de pointer-events de Radix.
 *   2. Fallback a `document.body` cuando NO está dentro de un Dialog.
 *   3. `container` se resuelve en efecto tras montar (post render) usando
 *      `wrapRef.current.closest('[role="dialog"]')` para tener el DOM listo.
 *   4. Se mantiene `onMouseDown={stopPropagation}` para evitar cierre por
 *      outside-click, y `requestAnimationFrame` en `handleSelect` para no
 *      desmontar el portal antes de que el click complete su propagación.
 */
import { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, X, Check } from 'lucide-react';

export const SearchableCombobox = ({
  items = [],
  value,
  onChange,
  placeholder = 'Buscar...',
  emptyMessage = 'Sin resultados',
  className = '',
  fallbackThreshold = 3,
  testid = 'searchable-combobox',
  compact = false,
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [openUpward, setOpenUpward] = useState(false);
  const [container, setContainer] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const selected = useMemo(
    () => items.find((i) => String(i.value) === String(value)),
    [items, value],
  );

  const norm = (s) => (s || '').toString().toLowerCase().trim();

  const { filtered, showAll } = useMemo(() => {
    const q = norm(query);
    if (!q) return { filtered: items, showAll: false };
    const tokens = q.split(/\s+/).filter(Boolean);
    const list = items.filter((i) => {
      const hay = `${norm(i.label)} ${norm(i.subLabel)} ${norm(i.value)}`;
      return tokens.every((t) => hay.includes(t));
    });
    if (list.length === 0 && q.length >= fallbackThreshold) {
      return { filtered: items, showAll: true };
    }
    return { filtered: list, showAll: false };
  }, [items, query, fallbackThreshold]);

  // Cierra al hacer click fuera (mousedown global). Como el dropdown detiene
  // la propagación en su onMouseDown, los clicks internos NO llegan acá.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;
    // Resuelve el contenedor del portal: si está dentro de un Radix Dialog
    // (aria-hidden y pointer-events lock), portaliza AL propio dialog para
    // que quede dentro del stacking context y del contenedor accesible.
    const dialogAncestor = wrapRef.current.closest('[role="dialog"]');
    setContainer(dialogAncestor || document.body);
    const compute = () => {
      const r = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const preferUp = spaceBelow < 220 && spaceAbove > spaceBelow;
      setOpenUpward(preferUp);
      setRect({
        left: r.left,
        top: r.top,
        bottom: r.bottom,
        width: r.width,
        spaceBelow,
        spaceAbove,
      });
    };
    compute();
    const onScroll = () => compute();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const handleSelect = (item) => {
    // 1) Notifica al parent SINCRÓNICAMENTE — setState del value se enfila.
    if (onChange) onChange(item);
    // 2) Difiere el cierre un frame para no interrumpir la propagación del click.
    requestAnimationFrame(() => {
      setQuery('');
      setOpen(false);
    });
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onChange) onChange(null);
    setQuery('');
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && filtered.length > 0) {
      e.preventDefault();
      handleSelect(filtered[0]);
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  const maxH = rect
    ? Math.min(260, openUpward ? rect.spaceAbove - 12 : rect.spaceBelow - 12)
    : 260;
  // Cuando el portal apunta a un Dialog Radix (con transform en su
  // DialogContent ancestor), `position: fixed` se posiciona relativo al
  // ancestor transformado, no al viewport. Detectamos si hay algún ancestor
  // con transform y ajustamos las coordenadas restando el rect del container.
  const containerIsDialog =
    container && container !== document.body && container.getBoundingClientRect;
  const cRect = containerIsDialog ? container.getBoundingClientRect() : null;
  const dropdownStyle = rect
    ? containerIsDialog
      ? {
          // Dentro de un Dialog: absolute con coordenadas locales al container
          position: 'absolute',
          left: rect.left - cRect.left,
          width: rect.width,
          ...(openUpward
            ? { bottom: cRect.bottom - rect.top + 2 }
            : { top: rect.bottom - cRect.top + 2 }),
          maxHeight: Math.max(120, maxH),
          zIndex: 9999,
        }
      : {
          position: 'fixed',
          left: rect.left,
          width: rect.width,
          ...(openUpward
            ? { bottom: window.innerHeight - rect.top + 2 }
            : { top: rect.bottom + 2 }),
          maxHeight: Math.max(120, maxH),
          zIndex: 9999,
        }
    : { display: 'none' };

  const inputDisplay = query !== '' ? query : selected?.label || '';

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testid}>
      <div className="relative flex items-center">
        {!compact && (
          <Search className="absolute left-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        )}
        <input
          ref={inputRef}
          type="text"
          value={inputDisplay}
          disabled={disabled}
          onFocus={(e) => {
            if (disabled) return;
            setOpen(true);
            try {
              e.target.select();
            } catch {
              /* noop */
            }
          }}
          onChange={(e) => {
            if (disabled) return;
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={disabled ? undefined : handleKeyDown}
          placeholder={placeholder}
          className={`w-full text-xs border rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
            compact ? 'h-7 pl-2 pr-7' : 'h-8 pl-7 pr-14'
          } ${selected ? 'border-green-400 bg-green-50/30' : 'border-gray-300'} ${
            disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''
          }`}
          data-testid={`${testid}-input`}
        />
        {selected && (
          <button
            type="button"
            onMouseDown={handleClear}
            className={`absolute ${compact ? 'right-5' : 'right-6'} text-gray-400 hover:text-red-500`}
            data-testid={`${testid}-clear`}
            title="Limpiar"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <ChevronDown className={`absolute ${compact ? 'right-1' : 'right-2'} w-3.5 h-3.5 text-gray-400 pointer-events-none`} />
      </div>
      {open &&
        rect &&
        container &&
        createPortal(
          <div
            ref={dropdownRef}
            className="overflow-auto border border-gray-200 rounded bg-white shadow-lg"
            style={dropdownStyle}
            data-testid={`${testid}-dropdown`}
            // CRITICAL: previene que el mousedown salga del dropdown y llegue
            // al outside-click listener global (que cerraría el dropdown antes
            // de que el click en la option se dispare).
            onMouseDown={(e) => e.stopPropagation()}
          >
            {showAll && (
              <div className="px-3 py-1.5 text-[10px] text-amber-700 bg-amber-50 border-b border-amber-200 sticky top-0">
                Sin coincidencias con &quot;{query}&quot; — mostrando listado
                completo ({items.length})
              </div>
            )}
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-center text-gray-400">
                {emptyMessage}
              </div>
            ) : (
              filtered.map((item) => (
                <button
                  key={String(item.value)}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className={`w-full flex items-start justify-between gap-2 px-3 py-1.5 text-xs text-left hover:bg-blue-50 ${
                    String(item.value) === String(value)
                      ? 'bg-blue-100 font-medium'
                      : ''
                  }`}
                  data-testid={`${testid}-option-${item.value}`}
                >
                  <span className="flex-1">
                    <span className="block">{item.label}</span>
                    {item.subLabel && (
                      <span className="block text-[10px] text-gray-500">
                        {item.subLabel}
                      </span>
                    )}
                  </span>
                  {String(item.value) === String(value) && (
                    <Check className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>,
          container,
        )}
    </div>
  );
};

export default SearchableCombobox;
